import { Skeleton } from '@/components/ui/skeleton';

/**
 * Loading placeholder mirroring ReviewPageEditorLayout: a fixed-width left editor
 * column (title, 3-tab rail, labeled field stack, footer buttons) and a right
 * preview pane. Uses the layout's real container classes so swapping to the live
 * editor doesn't shift anything.
 *
 * Lives in its own light module (no editor logic) so the /reviews/pages resolver
 * can render the EXACT same skeleton during its resolve+redirect window without
 * pulling the heavy editor into the resolver's chunk — keeping the public-page
 * editor a single, continuous skeleton.
 */
export function ReviewPageEditorSkeleton() {
  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden bg-background">
      {/* Two-column body: controls (left) | preview (right). The page header
          (title + subtitle) lives INSIDE the left column. Mirrors
          ReviewPageEditorLayout so the skeleton→live swap doesn't shift. */}
      <div className="relative flex flex-1 min-h-0 flex-col overflow-hidden lg:flex-row">
        {/* Left editor column — fixed 460px on lg+ (matches PANEL_WIDTH), full-width below. */}
        <div className="shrink-0 border-b border-border bg-card lg:w-[460px] lg:border-b-0 lg:border-r">
          <div className="flex h-full flex-col">
            {/* Tab rail — title/subtitle removed (the breadcrumb names the page). */}
            <div className="shrink-0 bg-card pt-4">
              {/* Tab rail — 3 stage tabs. The 36px height is the live
                  TabsList's, measured: a shorter placeholder row shifted every
                  tab down by the difference the moment the editor mounted. */}
              <div className="flex h-9 items-center gap-6 border-b border-border px-5">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>

            {/* Body: labeled field stack, centered + width-capped like the real panel */}
            <div className="flex-1 overflow-hidden bg-background/40">
              <div className="flex justify-center py-6 px-4">
                <div className="w-full max-w-md space-y-5">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="space-y-1.5">
                      <Skeleton className="h-3.5 w-24" />
                      <Skeleton className="h-9 w-full" />
                    </div>
                  ))}
                  <Skeleton className="h-px w-full" />
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <Skeleton className="h-9 flex-1" />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer: Save / Cancel buttons */}
            <div className="shrink-0 border-t border-border bg-card px-5 py-3 flex items-center gap-2">
              <Skeleton className="h-9 flex-1" />
              <Skeleton className="h-9 w-20" />
            </div>
          </div>
        </div>

        {/* Right preview pane — mirrors the funnel card (centred card, avatar,
            heading, thumb buttons, footer) rather than one tall block, so the
            swap to the real preview doesn't jump.
            Centred, like the live preview — a top-aligned placeholder dropped
            the card into the middle of the pane the moment the data landed. */}
        <div className="flex flex-1 min-w-0 min-h-0 items-center justify-center overflow-hidden bg-muted/20 px-4">
          <div className="w-full max-w-sm">
            <div className="w-full rounded-3xl bg-card p-8 shadow-sm">
              <div className="flex flex-col items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="w-full space-y-2">
                  <Skeleton className="mx-auto h-6 w-3/4" />
                  <Skeleton className="mx-auto h-6 w-1/2" />
                </div>
                <Skeleton className="mx-auto h-4 w-40" />
                <div className="flex items-center justify-center gap-4 py-2">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <Skeleton className="h-16 w-16 rounded-full" />
                </div>
                <div className="w-full space-y-1.5 pt-6">
                  <Skeleton className="mx-auto h-3.5 w-36" />
                  <Skeleton className="mx-auto h-3.5 w-52" />
                  <Skeleton className="mx-auto h-3 w-28" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
